/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState, useEffect } from "react";
import useForm from "react-hook-form";
import { Auth } from "@aws-amplify/auth";
import { FormProps } from "aws-amplify-react/props";
import { Form, Button } from "semantic-ui-react";
import cStyles from "../Components.module.css";
import styles from "./LandingPage/LandingForm/LandingForm.module.css";
import {
  notify,
  usernameFromAuthData,
  emailValidator,
  passwordValidator,
  codeValidator,
} from "./utils";
import { fieldContainer } from "./FieldContainer";
import { useTranslation } from "react-i18next";

type FormData = {
  username: string;
  code: string;
  password: string;
};

const ForgotPassword: React.FC<FormProps> = ({
  authData = {},
  authState = "",
  onStateChange,
  onAuthEvent,
  hide = [],
  setLoading,
  setMessage,
  loading,
}) => {
  const { register, handleSubmit, errors, getValues } = useForm<FormData>();
  const [username, setUsername] = useState(usernameFromAuthData(authData));
  const [attemptRecoveryActive, setAttemptRecoveryActive] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  useEffect(() => {}, [attemptRecoveryActive]);
  const { changeState } = notify(authState, onAuthEvent, onStateChange);
  const { t } = useTranslation(["forgotPassword", "common"]);

  const validAuthStates = ["forgotPassword"];
  if (!validAuthStates.includes(authState)) {
    return null;
  }

  const requestRecovery = () => {
    const data = getValues();
    if (!Auth || typeof Auth.forgotPassword !== "function") {
      throw new Error(
        "No Auth module found, please ensure @aws-amplify/auth is imported"
      );
    }

    setMessage(null);
    setLoading(true);
    data.username = data.username.toLocaleLowerCase();
    Auth.forgotPassword(data.username)
      .then(() => {
        setUsername(data.username);
        setAttemptRecoveryActive(true);
      })
      .catch((err) => {
        if (err.code === "UserNotFoundException") {
          setMessage(t("invalid_email"));
          return;
        }
        setMessage(err.message || err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const attemptRecovery = (data: FormData) => {
    const { code, password } = data;
    if (!Auth || typeof Auth.forgotPasswordSubmit !== "function") {
      throw new Error(
        "No Auth module found, please ensure @aws-amplify/auth is imported"
      );
    }

    setLoading(true);
    Auth.forgotPasswordSubmit(username, code, password)
      .then(() => {
        changeState("signIn");
        setMessage(t("reset_success"));
      })
      .catch((err) => setMessage(err.message || err))
      .finally(() => {
        setLoading(false);
      });
  };

  if (hide && hide.includes(ForgotPassword)) {
    return null;
  }

  const requestRecoveryView = () => (
    <React.Fragment>
      <Form
        onSubmit={handleSubmit(requestRecovery)}
        className={styles.signupForm}
      >
        <div className={styles.formHint}>{t("enter_email_to_reset")}</div>
        <Form.Group inline className={styles.formGroup}>
          <Form.Field
            required
            error={!!errors.username}
            className={styles.formField}
          >
            {fieldContainer(
              { label: t("common:email"), errors },
              {
                autoFocus: true,
                name: "username",
                key: "username",
                placeholder: t("common:enter_email"),
                ref: register({
                  required: t("common:email_required") as string,
                  pattern: {
                    value: emailValidator,
                    message: t("common:valid_email"),
                  },
                }),
                defaultValue: username,
                autoComplete: "email",
                disabled: loading,
              }
            )}
          </Form.Field>
        </Form.Group>

        <div style={{ textAlign: "right" }}>
          <span
            className={styles.formLink}
            onClick={() => {
              setMessage(null);
              changeState("signIn");
            }}
          >
            {t("back")}
          </span>
        </div>

        <Button
          id="resetPasswordButton"
          className={`${cStyles.lpButton} ${styles.submitFormButton}`}
          style={{
            marginTop: "4rem",
          }}
          loading={loading}
          disabled={loading}
          onClick={() => {
            setMessage(null);
          }}
        >
          {t("reset_password")}
        </Button>
      </Form>
    </React.Fragment>
  );

  const attemptRecoveryView = () => (
    <React.Fragment>
      <Form
        onSubmit={handleSubmit(attemptRecovery)}
        className={styles.signupForm}
      >
        <div className={styles.formHint}>{t("set_new_password")}</div>

        <Form.Group inline className={styles.formGroup}>
          <Form.Field
            required
            error={!!errors.code}
            className={styles.formField}
          >
            {fieldContainer(
              { label: t("common:code"), errors },
              {
                autoFocus: true,
                name: "code",
                key: "code",
                placeholder: t("common:enter_code"),
                autoComplete: "off",
                ref: register({
                  required: t("common:code_required") as string,
                  pattern: {
                    value: codeValidator,
                    message: t("common:valid_code"),
                  },
                }),
              }
            )}
          </Form.Field>
        </Form.Group>

        <div style={{ textAlign: "right" }}>
          <span
            className={styles.formLink}
            onClick={() => {
              setMessage(null);
              setAttemptRecoveryActive(false);
            }}
          >
            {t("common:resend_code")}
          </span>
        </div>

        <Form.Group inline className={styles.formGroup}>
          <Form.Field
            required
            error={!!errors.password}
            className={styles.formField}
          >
            {fieldContainer(
              {
                label: t("common:password"),
                errors,
                actionIcon: showPassword ? "eye slash" : "eye",
                actionIconAction: () => {
                  setShowPassword(!showPassword);
                },
              },
              {
                name: "password",
                key: "password",
                type: showPassword ? "text" : "password",
                placeholder: t("common:enter_new_password"),
                autoComplete: "new-password",

                ref: register({
                  required: t("new_password_required") as string,
                  pattern: {
                    value: passwordValidator,
                    message: "",
                  },
                }),
              }
            )}
          </Form.Field>
        </Form.Group>

        <div
          className={
            errors?.password?.type === "pattern"
              ? styles.fieldAlwaysVisibleError
              : styles.fieldAlwaysVisible
          }
        >
          {t("common:valid_password")}
        </div>

        <Form.Group inline className={styles.formGroup}>
          <Form.Field
            required
            error={!!errors.confirmPassword}
            className={styles.formField}
          >
            {fieldContainer(
              { label: t("common:confirm"), errors },
              {
                name: "confirmPassword",
                key: "confirmPassword",
                type: "password",
                placeholder: t("common:confirm_password"),
                autoComplete: "off",
                ref: register({
                  required: t("password_confirm_required") as string,
                  validate: (value) =>
                    value === getValues().password ||
                    (t("common:password_no_match") as string),
                }),
              }
            )}
          </Form.Field>
        </Form.Group>

        <Button
          className={`${cStyles.lpButton} ${styles.submitFormButton}`}
          loading={loading}
          disabled={loading}
          id="setNewPasswordButton"
        >
          {t("set_new_password_title")}
        </Button>
      </Form>
    </React.Fragment>
  );

  return attemptRecoveryActive ? attemptRecoveryView() : requestRecoveryView();
};

export default ForgotPassword;
